<template>
  <v-card class="management-trend-widget" elevation="3" style="height: 100%;">
    <v-card-title class="d-flex align-center justify-space-between pa-3">
      <span style="font-size: 1rem; font-weight: 600;">Trend-Analyse</span>

      <!-- Mini-Toggle für Zeitraum-Auswahl -->
      <div class="mini-toggle">
        <button
          class="mini-btn"
          :class="{ 'mini-btn-active': selectedPeriod === 'weekly' }"
          @click="selectedPeriod = 'weekly'"
          title="Wöchentlich">
          W
        </button>
        <button
          class="mini-btn"
          :class="{ 'mini-btn-active': selectedPeriod === 'monthly' }"
          @click="selectedPeriod = 'monthly'"
          title="Monatlich">
          M
        </button>
      </div>
    </v-card-title>

    <v-card-text class="pa-2" style="height: calc(100% - 60px);">
      <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%;">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="error" class="text-center text-error">
        <v-icon>mdi-alert-circle</v-icon>
        <div>{{ error }}</div>
      </div>

      <div v-else-if="!trendData || trendData.length === 0" class="text-center" style="padding: 40px;">
        <v-icon size="48" color="grey">mdi-chart-line</v-icon>
        <div class="text-subtitle-2 text-medium-emphasis mt-2">
          Keine Daten verfügbar
        </div>
      </div>

      <div v-else class="trend-overview">
        <!-- Chart-Vorschau mit Button -->
        <div class="chart-preview">
          <div class="preview-icon">
            <v-icon size="60" color="primary">mdi-chart-line</v-icon>
          </div>
          <div class="preview-text">
            <div class="preview-title">Mengen-Entwicklung</div>
            <div class="preview-subtitle">
              {{ selectedPeriod === 'weekly' ? 'Wöchentliche' : 'Monatliche' }} Trends
            </div>
            <v-btn
              color="primary"
              variant="tonal"
              @click="showChartModal = true"
              class="mt-2"
              :loading="chartLoading">
              <v-icon start>mdi-chart-line</v-icon>
              Chart öffnen
            </v-btn>
          </div>
        </div>

        <!-- Letzte 4 Perioden - Ultra-kompakt -->
        <div class="period-summary">
          <div class="period-header">Letzte {{ selectedPeriod === 'weekly' ? '4 Wochen' : '4 Monate' }}</div>
          <div class="period-list">
            <div
              v-for="(item, index) in trendData.slice(-4)"
              :key="index"
              class="period-item">
              <div class="period-label">{{ item.label }}</div>
              <div class="period-value">{{ item.total_menge }}</div>
            </div>
          </div>
        </div>

        <!-- Trend-Richtung -->
        <div class="trend-direction">
          <v-icon :color="trendDirection.color" size="20">{{ trendDirection.icon }}</v-icon>
          <span class="ml-2">{{ trendDirection.text }}</span>
        </div>

        <!-- Durchschnittswerte - Grid -->
        <div class="averages-grid">
          <div class="average-item">
            <div class="average-value">{{ Math.round(averages?.total_menge || 0) }}</div>
            <div class="average-label">⌀ Total</div>
          </div>
          <div class="average-item">
            <div class="average-value">{{ Math.round(averages?.instandsetzungen_menge || 0) }}</div>
            <div class="average-label">⌀ Instand.</div>
          </div>
          <div class="average-item">
            <div class="average-value">{{ Math.round(averages?.retouren_menge || 0) }}</div>
            <div class="average-label">⌀ Retouren</div>
          </div>
          <div class="average-item">
            <div class="average-value">{{ formatMinutes(averages?.nebentaetigkeiten_minuten || 0) }}</div>
            <div class="average-label">⌀ Neben.</div>
          </div>
        </div>

        <!-- Detaillierte Aufschlüsselung -->
        <div class="detailed-breakdown">
          <div class="breakdown-header">
            <v-icon size="16" class="mr-1">mdi-format-list-bulleted</v-icon>
            Letzte abgeschlossene Periode
          </div>
          <div v-if="latestPeriod" class="breakdown-content">
            <div class="breakdown-row">
              <span class="breakdown-label">Periode:</span>
              <span class="breakdown-value">{{ latestPeriod.full_label }}</span>
            </div>
            <div class="breakdown-row">
              <span class="breakdown-label">Gesamtmenge:</span>
              <span class="breakdown-value highlight">{{ latestPeriod.total_menge }} Stk.</span>
            </div>
            <div class="breakdown-section">
              <div class="breakdown-subheader">Instandsetzungen</div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Menge:</span>
                <span class="breakdown-value">{{ latestPeriod.instandsetzungen_menge }} Stk.</span>
              </div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Aufträge:</span>
                <span class="breakdown-value">{{ latestPeriod.instandsetzungen }}</span>
              </div>
            </div>
            <div class="breakdown-section">
              <div class="breakdown-subheader">Retouren</div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Menge:</span>
                <span class="breakdown-value">{{ latestPeriod.retouren_menge }} Stk.</span>
              </div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Aufträge:</span>
                <span class="breakdown-value">{{ latestPeriod.retouren }}</span>
              </div>
            </div>
            <div class="breakdown-section">
              <div class="breakdown-subheader">Nebentätigkeiten</div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Zeit:</span>
                <span class="breakdown-value">{{ formatMinutes(latestPeriod.nebentaetigkeiten_minuten) }}</span>
              </div>
              <div class="breakdown-row">
                <span class="breakdown-label">→ Einträge:</span>
                <span class="breakdown-value">{{ latestPeriod.nebentaetigkeiten_count }}</span>
              </div>
            </div>
            <div class="breakdown-row productivity">
              <span class="breakdown-label">Gesamtleistung:</span>
              <span class="breakdown-value">{{ calculateProductivity(latestPeriod) }} Stk.</span>
            </div>

            <!-- Details Button -->
            <v-btn
              v-if="latestPeriod"
              @click="fetchPeriodDetails(latestPeriod)"
              color="success"
              variant="tonal"
              size="small"
              block
              class="mt-3"
              :loading="detailsLoading">
              <v-icon size="small" class="mr-1">mdi-clipboard-list-outline</v-icon>
              Details anzeigen
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>

    <!-- Chart Modal -->
    <v-dialog v-model="showChartModal" max-width="1200px" max-height="800px" fullscreen>
      <v-card class="chart-modal">
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <div>
            <h2>Trend-Analyse: {{ props.employee.name }}</h2>
            <p class="text-subtitle-1 text-medium-emphasis ma-0">
              {{ selectedPeriod === 'weekly' ? 'Wöchentliche Entwicklung' : 'Monatliche Entwicklung' }}
            </p>
          </div>
          <v-btn icon="mdi-close" @click="showChartModal = false" variant="text"></v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div v-if="trendData && trendData.length > 0" class="chart-modal-content">
            <!-- Chart Container -->
            <div class="modal-chart-container" ref="chartContainer">
              <h3 class="text-h5 mb-4 text-center">Mengen-Entwicklung</h3>
              <div class="chart-wrapper" @mouseleave="hideTooltip">
                <svg
                  :width="modalChartWidth"
                  :height="modalChartHeight"
                  class="modal-chart-svg"
                  ref="chartSvg">

                  <!-- Gradient Definition -->
                  <defs>
                    <linearGradient id="modalChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" :style="`stop-color:${chartColor.value};stop-opacity:0.3`" />
                      <stop offset="100%" :style="`stop-color:${chartColor.value};stop-opacity:0.05`" />
                    </linearGradient>
                  </defs>

                  <!-- Grid Lines -->
                  <g v-for="tick in yAxisTicks" :key="tick.value">
                    <line
                      :x1="modalPadding"
                      :y1="tick.y"
                      :x2="modalChartWidth - modalPadding"
                      :y2="tick.y"
                      stroke="rgba(255,255,255,0.1)"
                      stroke-width="1"/>
                    <text
                      :x="modalPadding - 10"
                      :y="tick.y + 5"
                      text-anchor="end"
                      fill="rgba(255,255,255,0.6)"
                      font-size="14">{{ tick.value }}</text>
                  </g>

                  <!-- X-Axis Labels -->
                  <g v-for="(point, index) in modalChartPoints" :key="index">
                    <text
                      :x="point.x"
                      :y="modalChartHeight - modalPadding + 20"
                      text-anchor="middle"
                      fill="rgba(255,255,255,0.7)"
                      font-size="12">{{ trendData[index]?.label || '' }}</text>
                  </g>

                  <!-- Average Line -->
                  <line
                    :x1="modalPadding"
                    :y1="avgLineY"
                    :x2="modalChartWidth - modalPadding"
                    :y2="avgLineY"
                    stroke="rgba(255,255,255,0.4)"
                    stroke-width="2"
                    stroke-dasharray="8,4"/>
                  <text
                    :x="modalChartWidth - modalPadding + 5"
                    :y="avgLineY + 5"
                    fill="rgba(255,255,255,0.6)"
                    font-size="12">⌀ {{ Math.round(averages?.total_menge || 0) }}</text>

                  <!-- Area Path -->
                  <path
                    :d="modalAreaPath"
                    fill="url(#modalChartGradient)"/>

                  <!-- Line Path -->
                  <path
                    :d="modalLinePath"
                    :stroke="chartColor.value"
                    stroke-width="4"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>

                  <!-- Data Points -->
                  <g v-for="(point, index) in modalChartPoints" :key="index">
                    <circle
                      :cx="point.x"
                      :cy="point.y"
                      r="8"
                      :fill="chartColor.value"
                      stroke="white"
                      stroke-width="3"
                      class="modal-chart-point"
                      style="cursor: pointer;"
                      @mouseenter="showTooltip($event, index)"
                      @mouseleave="hideTooltip"
                      @click="fetchPeriodDetails(trendData[index])"/>
                    <circle
                      :cx="point.x"
                      :cy="point.y"
                      r="20"
                      fill="transparent"
                      class="modal-chart-hover-area"
                      style="cursor: pointer;"
                      @mouseenter="showTooltip($event, index)"
                      @mouseleave="hideTooltip"
                      @click="fetchPeriodDetails(trendData[index])"/>
                  </g>
                </svg>

                <!-- Tooltip -->
                <div
                  v-if="tooltipVisible"
                  ref="tooltip"
                  class="modal-chart-tooltip"
                  :style="tooltipStyle">
                  <div class="tooltip-header">{{ tooltipData?.full_label }}</div>
                  <div class="tooltip-content">
                    <div class="tooltip-section">
                      <div class="tooltip-row total-row">
                        <span class="tooltip-label">📊 Gesamtmenge:</span>
                        <span class="tooltip-value">{{ tooltipData?.total_menge }} Stk.</span>
                      </div>
                      <div class="tooltip-row total-row">
                        <span class="tooltip-label">📋 Aufträge gesamt:</span>
                        <span class="tooltip-value">{{ tooltipTotalAuftraege }} Aufträge</span>
                      </div>
                    </div>
                    <div class="tooltip-divider"></div>
                    <div class="tooltip-section">
                      <div class="tooltip-row">
                        <span class="tooltip-label">🔧 Instandsetzungen:</span>
                        <span class="tooltip-value">{{ tooltipData?.instandsetzungen_menge }} Stk. ({{ tooltipData?.instandsetzungen }} Aufträge)</span>
                      </div>
                      <div class="tooltip-row">
                        <span class="tooltip-label">↩️ Retouren:</span>
                        <span class="tooltip-value">{{ tooltipData?.retouren_menge }} Stk. ({{ tooltipData?.retouren }} Aufträge)</span>
                      </div>
                      <div class="tooltip-row">
                        <span class="tooltip-label">⏱️ Nebentätigkeiten:</span>
                        <span class="tooltip-value">{{ formatMinutes(tooltipData?.nebentaetigkeiten_minuten) }} ({{ tooltipData?.nebentaetigkeiten_count }} Einträge)</span>
                      </div>
                    </div>
                    <div class="tooltip-divider"></div>
                    <div class="tooltip-section">
                      <div class="tooltip-row productivity-row">
                        <span class="tooltip-label">⚡ Gesamtleistung:</span>
                        <span class="tooltip-value">{{ tooltipProductivity }} Stk.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics Grid -->
            <div class="modal-stats-grid mt-6">
              <div class="modal-stat-card">
                <div class="stat-value">{{ Math.round(averages?.total_menge || 0) }}</div>
                <div class="stat-label">⌀ Gesamtmenge</div>
              </div>
              <div class="modal-stat-card">
                <div class="stat-value">{{ Math.round(averages?.instandsetzungen_menge || 0) }}</div>
                <div class="stat-label">⌀ Instandsetzungen</div>
              </div>
              <div class="modal-stat-card">
                <div class="stat-value">{{ Math.round(averages?.retouren_menge || 0) }}</div>
                <div class="stat-label">⌀ Retouren</div>
              </div>
              <div class="modal-stat-card">
                <div class="stat-value">{{ formatMinutes(averages?.nebentaetigkeiten_minuten || 0) }}</div>
                <div class="stat-label">⌀ Nebentätigkeiten</div>
              </div>
            </div>

            <!-- Data Table -->
            <div class="modal-data-table mt-6">
              <h3 class="text-h6 mb-4 text-center">Detaillierte Aufschlüsselung</h3>
              <v-table density="compact" theme="dark">
                <thead>
                  <tr>
                    <th>Zeitraum</th>
                    <th>Gesamtmenge</th>
                    <th>Instandsetzungen</th>
                    <th>Retouren</th>
                    <th>Nebentätigkeiten</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in trendData" :key="item.label">
                    <td><strong>{{ item.full_label }}</strong></td>
                    <td>{{ item.total_menge }} Stk. ({{ item.total_auftraege }} Aufträge)</td>
                    <td>{{ item.instandsetzungen_menge }} Stk. ({{ item.instandsetzungen }} Aufträge)</td>
                    <td>{{ item.retouren_menge }} Stk. ({{ item.retouren }} Aufträge)</td>
                    <td>{{ formatMinutes(item.nebentaetigkeiten_minuten) }} ({{ item.nebentaetigkeiten_count }} Einträge)</td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Details Modal -->
    <v-dialog v-model="showDetailsModal" max-width="1400px" scrollable>
      <v-card class="details-modal">
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <div>
            <div class="text-h5">
              <v-icon color="success" class="mr-2">mdi-clipboard-list</v-icon>
              Auftragsdetails
            </div>
            <div class="text-subtitle-2 mt-1" v-if="selectedPeriodData">
              {{ selectedPeriodData.full_label }}
            </div>
          </div>
          <v-btn icon @click="closeDetailsModal" variant="text">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4" style="max-height: 70vh;">
          <div v-if="detailsLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="success"></v-progress-circular>
            <div class="mt-4">Lade Details...</div>
          </div>

          <div v-else-if="periodDetails">
            <!-- Zusammenfassung -->
            <v-card class="mb-4" elevation="2">
              <v-card-title class="text-subtitle-1 pa-3 bg-grey-darken-3">
                <v-icon size="small" class="mr-2">mdi-chart-box</v-icon>
                Zusammenfassung
              </v-card-title>
              <v-card-text class="pa-3">
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Gesamtmenge</div>
                      <div class="summary-value text-success">{{ periodDetails.summary.total_menge }} Stk.</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Aufträge gesamt</div>
                      <div class="summary-value">{{ periodDetails.summary.total_auftraege }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Instandsetzungen</div>
                      <div class="summary-value">{{ periodDetails.summary.instandsetzungen_count }} ({{ periodDetails.summary.instandsetzungen_menge }} Stk.)</div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="summary-item">
                      <div class="summary-label">Retouren</div>
                      <div class="summary-value">{{ periodDetails.summary.retouren_count }} ({{ periodDetails.summary.retouren_menge }} Stk.)</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Tabs für verschiedene Kategorien -->
            <v-tabs v-model="detailsTab" color="success" class="mb-4">
              <v-tab value="instandsetzungen">
                <v-icon size="small" class="mr-2">mdi-wrench</v-icon>
                Instandsetzungen ({{ periodDetails.instandsetzungen.length }})
              </v-tab>
              <v-tab value="retouren">
                <v-icon size="small" class="mr-2">mdi-package-variant</v-icon>
                Retouren ({{ periodDetails.retouren.length }})
              </v-tab>
              <v-tab value="nebentaetigkeiten">
                <v-icon size="small" class="mr-2">mdi-clock-outline</v-icon>
                Nebentätigkeiten ({{ periodDetails.nebentaetigkeiten.length }})
              </v-tab>
            </v-tabs>

            <v-window v-model="detailsTab">
              <!-- Instandsetzungen -->
              <v-window-item value="instandsetzungen">
                <v-card elevation="2">
                  <v-data-table
                    :headers="instandsetzungenHeaders"
                    :items="periodDetails.instandsetzungen"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable"
                  >
                    <template v-slot:item.erstelldatum="{ item }">
                      {{ formatDate(item.erstelldatum) }}
                    </template>
                    <template v-slot:item.menge="{ item }">
                      <v-chip size="small" color="success" variant="flat">
                        {{ item.menge }} Stk.
                      </v-chip>
                    </template>
                    <template v-slot:item.notiz="{ item }">
                      <div class="text-truncate" style="max-width: 200px;" :title="item.notiz">
                        {{ item.notiz || '-' }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>

              <!-- Retouren -->
              <v-window-item value="retouren">
                <v-card elevation="2">
                  <v-data-table
                    :headers="retourenHeaders"
                    :items="periodDetails.retouren"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable"
                  >
                    <template v-slot:item.erstelldatum="{ item }">
                      {{ formatDate(item.erstelldatum) }}
                    </template>
                    <template v-slot:item.menge="{ item }">
                      <v-chip size="small" color="info" variant="flat">
                        {{ item.menge }} Stk.
                      </v-chip>
                    </template>
                    <template v-slot:item.notiz="{ item }">
                      <div class="text-truncate" style="max-width: 200px;" :title="item.notiz">
                        {{ item.notiz || '-' }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>

              <!-- Nebentätigkeiten -->
              <v-window-item value="nebentaetigkeiten">
                <v-card elevation="2">
                  <v-data-table
                    :headers="nebentaetigkeitenHeaders"
                    :items="periodDetails.nebentaetigkeiten"
                    :items-per-page="10"
                    class="details-table"
                    density="comfortable"
                  >
                    <template v-slot:item.startzeit="{ item }">
                      {{ formatDate(item.startzeit) }} {{ formatTime(item.startzeit) }}
                    </template>
                    <template v-slot:item.endzeit="{ item }">
                      {{ formatTime(item.endzeit) }}
                    </template>
                    <template v-slot:item.dauer_minuten="{ item }">
                      <v-chip size="small" color="warning" variant="flat">
                        {{ formatMinutes(item.dauer_minuten) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card>
              </v-window-item>
            </v-window>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import { API_BASE_URL } from '@/config'

const props = defineProps({
  employee: {
    type: Object,
    required: true
  },
  timeframe: {
    type: String,
    default: 'month'
  },
  selectedMonth: {
    type: Number,
    default: () => new Date().getMonth() + 1
  },
  selectedYear: {
    type: Number,
    default: () => new Date().getFullYear()
  }
})

// Reactive state
const loading = ref(false)
const error = ref(null)
const chartLoading = ref(false)
const trendData = ref([])
const averages = ref(null)
const selectedPeriod = ref('weekly')
const showChartModal = ref(false)
const showDetailsModal = ref(false)
const detailsLoading = ref(false)
const periodDetails = ref(null)
const selectedPeriodData = ref(null)
const tooltipVisible = ref(false)
const tooltipData = ref(null)
const tooltipStyle = ref({})

// Modal Chart Dimensions
const modalChartWidth = ref(1000)
const modalChartHeight = ref(400)
const modalPadding = ref(60)

// Chart-Farbe basierend auf Trend
const chartColor = computed(() => {
  if (!averages.value) return { value: '#4fc3f7' }

  const totalTrend = averages.value.total_menge || 0
  if (totalTrend > 100) return { value: '#4caf50' }  // Grün für hohe Werte
  if (totalTrend > 50) return { value: '#ff9800' }   // Orange für mittlere Werte
  return { value: '#f44336' }                        // Rot für niedrige Werte
})

// Letzte Periode für detaillierte Ansicht
const latestPeriod = computed(() => {
  if (!trendData.value || trendData.value.length === 0) return null
  // Nimm die vorletzte Periode (die zuletzt abgeschlossene), nicht die neueste
  if (trendData.value.length === 1) return trendData.value[0]
  return trendData.value[trendData.value.length - 2]
})

// Modal Chart Data
const modalChartData = computed(() => {
  if (!trendData.value || trendData.value.length === 0) return []
  const dataToUse = [...trendData.value]
  if (dataToUse.length === 1) {
    dataToUse.push({...dataToUse[0]})
  }
  return dataToUse
})

const modalChartRange = computed(() => {
  if (modalChartData.value.length === 0) return { min: 0, max: 1, range: 1 }
  const maxValue = Math.max(...modalChartData.value.map(d => d.total_menge || 0), 1)
  const minValue = Math.min(...modalChartData.value.map(d => d.total_menge || 0), 0)
  const range = maxValue - minValue || 1
  return { min: minValue, max: maxValue, range }
})

const modalChartPoints = computed(() => {
  return modalChartData.value.map((item, index) => ({
    x: modalPadding.value + (index * (modalChartWidth.value - modalPadding.value * 2)) / Math.max(modalChartData.value.length - 1, 1),
    y: modalChartHeight.value - modalPadding.value - ((item.total_menge - modalChartRange.value.min) / modalChartRange.value.range) * (modalChartHeight.value - modalPadding.value * 2)
  }))
})

const modalLinePath = computed(() => {
  if (modalChartPoints.value.length === 0) return ''
  if (modalChartPoints.value.length === 1) {
    const point = modalChartPoints.value[0]
    return `M ${modalPadding.value} ${point.y} L ${modalChartWidth.value - modalPadding.value} ${point.y}`
  }

  let path = `M ${modalChartPoints.value[0].x} ${modalChartPoints.value[0].y}`
  for (let i = 1; i < modalChartPoints.value.length; i++) {
    const prev = modalChartPoints.value[i - 1]
    const curr = modalChartPoints.value[i]
    const cpX1 = prev.x + (curr.x - prev.x) * 0.3
    const cpY1 = prev.y
    const cpX2 = curr.x - (curr.x - prev.x) * 0.3
    const cpY2 = curr.y
    path += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${curr.x} ${curr.y}`
  }
  return path
})

const modalAreaPath = computed(() => {
  if (modalChartPoints.value.length === 0) return ''
  if (modalChartPoints.value.length === 1) {
    const point = modalChartPoints.value[0]
    return `M ${modalPadding.value} ${point.y} L ${modalChartWidth.value - modalPadding.value} ${point.y} L ${modalChartWidth.value - modalPadding.value} ${modalChartHeight.value - modalPadding.value} L ${modalPadding.value} ${modalChartHeight.value - modalPadding.value} Z`
  }

  let path = modalLinePath.value
  const lastPoint = modalChartPoints.value[modalChartPoints.value.length - 1]
  const firstPoint = modalChartPoints.value[0]
  path += ` L ${lastPoint.x} ${modalChartHeight.value - modalPadding.value}`
  path += ` L ${firstPoint.x} ${modalChartHeight.value - modalPadding.value}`
  path += ' Z'
  return path
})

const yAxisTicks = computed(() => {
  const ticks = []
  const tickCount = 8
  for (let i = 0; i <= tickCount; i++) {
    const value = Math.round(modalChartRange.value.min + (modalChartRange.value.range / tickCount) * i)
    const y = modalChartHeight.value - modalPadding.value - ((value - modalChartRange.value.min) / modalChartRange.value.range) * (modalChartHeight.value - modalPadding.value * 2)
    ticks.push({ value, y })
  }
  return ticks
})

const avgLineY = computed(() => {
  const avgValue = averages.value?.total_menge || 0
  return modalChartHeight.value - modalPadding.value - ((avgValue - modalChartRange.value.min) / modalChartRange.value.range) * (modalChartHeight.value - modalPadding.value * 2)
})

const tooltipTotalAuftraege = computed(() => {
  if (!tooltipData.value) return 0
  return (tooltipData.value.instandsetzungen || 0) + (tooltipData.value.retouren || 0) + (tooltipData.value.nebentaetigkeiten_count || 0)
})

const tooltipProductivity = computed(() => {
  if (!tooltipData.value) return '0'
  return (tooltipData.value.total_menge || 0).toString()
})

// Tab-State für Details-Modal
const detailsTab = ref('instandsetzungen')

// Table Headers für Details
const instandsetzungenHeaders = [
  { title: 'Auftragsnr.', key: 'auftragsnummer', sortable: true },
  { title: 'Datum', key: 'erstelldatum', sortable: true },
  { title: 'Kunde', key: 'kunde', sortable: true },
  { title: 'Gerätetyp', key: 'geraetetyp', sortable: true },
  { title: 'Reparaturtyp', key: 'reparaturtyp', sortable: true },
  { title: 'Menge', key: 'menge', sortable: true },
  { title: 'Notiz', key: 'notiz', sortable: false }
]

const retourenHeaders = [
  { title: 'Auftragsnr.', key: 'auftragsnummer', sortable: true },
  { title: 'Datum', key: 'erstelldatum', sortable: true },
  { title: 'Kunde', key: 'kunde', sortable: true },
  { title: 'Gerätetyp', key: 'geraetetyp', sortable: true },
  { title: 'Retourentyp', key: 'retourentyp', sortable: true },
  { title: 'Menge', key: 'menge', sortable: true },
  { title: 'Notiz', key: 'notiz', sortable: false }
]

const nebentaetigkeitenHeaders = [
  { title: 'Startzeit', key: 'startzeit', sortable: true },
  { title: 'Endzeit', key: 'endzeit', sortable: true },
  { title: 'Dauer', key: 'dauer_minuten', sortable: true },
  { title: 'Kommentar', key: 'kommentar', sortable: false }
]

// Trend-Richtung berechnen
const trendDirection = computed(() => {
  if (!trendData.value || trendData.value.length < 2) {
    return { icon: 'mdi-minus', color: 'grey', text: 'Keine Daten' }
  }

  const recent = trendData.value.slice(-2)
  const current = recent[1]?.total_menge || 0
  const previous = recent[0]?.total_menge || 0

  if (current > previous) {
    return {
      icon: 'mdi-trending-up',
      color: 'green',
      text: '+' + (current - previous) + ' Stk.'
    }
  } else if (current < previous) {
    return {
      icon: 'mdi-trending-down',
      color: 'red',
      text: (current - previous) + ' Stk.'
    }
  } else {
    return { icon: 'mdi-minus', color: 'grey', text: 'Unverändert' }
  }
})

// Zeit formatieren
const formatMinutes = (minutes) => {
  if (!minutes) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0) {
    return hours + 'h' + (mins > 0 ? mins : '')
  } else {
    return mins + 'm'
  }
}

// Produktivität berechnen
const calculateProductivity = (periodData) => {
  if (!periodData) return '0'
  // Einfach die Gesamtmenge zurückgeben, da es um Stückzahl geht, nicht um Aufträge
  return (periodData.total_menge || 0).toString()
}

// Modal Tooltip Functions
const showTooltip = (event, index) => {
  const item = trendData.value[index]
  if (!item) return

  tooltipData.value = item
  tooltipVisible.value = true

  const rect = event.target.getBoundingClientRect()
  const tooltipWidth = 280
  const tooltipHeight = 300

  let left = rect.left + (rect.width / 2) - (tooltipWidth / 2)
  let top = rect.top - tooltipHeight - 15

  // Boundary checks
  if (left < 10) left = 10
  if (left + tooltipWidth > window.innerWidth - 10) left = window.innerWidth - tooltipWidth - 10
  if (top < 10) top = rect.bottom + 15

  tooltipStyle.value = {
    left: left + 'px',
    top: top + 'px'
  }
}

const hideTooltip = () => {
  tooltipVisible.value = false
  tooltipData.value = null
}

// Detaillierte Auftragsliste für einen Zeitraum laden
const fetchPeriodDetails = async (periodData) => {
  if (!periodData || !props.employee?.id) return

  detailsLoading.value = true
  selectedPeriodData.value = periodData

  try {
    // Berechne Start und Ende des Zeitraums
    let periodStart, periodEnd

    if (selectedPeriod.value === 'weekly') {
      // Für wöchentliche Daten: period ist der Wochenstart
      periodStart = periodData.period
      const endDate = new Date(periodStart)
      endDate.setDate(endDate.getDate() + 6)
      periodEnd = endDate.toISOString().split('T')[0]
    } else {
      // Für monatliche Daten: period ist der Monatsanfang
      periodStart = periodData.period
      const date = new Date(periodStart)
      periodEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0]
    }

    const payload = {
      action: 'period_details',
      user_id: props.employee.id,
      period_start: periodStart,
      period_end: periodEnd
    }

    const response = await axios.post(`${API_BASE_URL}admin/management_stats.php`, payload, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (response.data.status === 'success') {
      periodDetails.value = response.data
      showDetailsModal.value = true
    } else {
      console.error('Fehler beim Laden der Details:', response.data.message)
      alert('Fehler beim Laden der Details: ' + (response.data.message || 'Unbekannter Fehler'))
    }
  } catch (err) {
    console.error('Fehler beim Laden der Periodendetails:', err)
    alert('Fehler beim Laden der Periodendetails: ' + (err.response?.data?.message || err.message))
  } finally {
    detailsLoading.value = false
  }
}

// Modal schließen
const closeDetailsModal = () => {
  showDetailsModal.value = false
  periodDetails.value = null
  selectedPeriodData.value = null
}

// Hilfsfunktion: Datum formatieren
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Hilfsfunktion: Zeit formatieren
const formatTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

// API-Aufruf für Trend-Daten
const fetchTrendData = async () => {
  if (!props.employee?.id) return

  loading.value = true
  error.value = null

  try {
    const payload = {
      action: 'trends',
      user_id: props.employee.id,
      trend_type: selectedPeriod.value,
      reference_month: props.selectedMonth,
      reference_year: props.selectedYear
    }

    const response = await axios.post(`${API_BASE_URL}admin/management_stats.php`, payload, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (response.data.status === 'success') {
      trendData.value = response.data.trends || []
      averages.value = response.data.averages || null
    } else {
      error.value = response.data.message || 'Fehler beim Laden der Daten'
    }
  } catch (err) {
    console.error('Fehler beim Laden der Trend-Daten:', err)
    error.value = 'Fehler beim Laden der Trend-Daten'
  } finally {
    loading.value = false
  }
}

// HTML für Chart-Fenster generieren
const generateChartHtml = () => {
  const largeChartWidth = 1000
  const largeChartHeight = 400
  const largePadding = 60

  // Chart-Daten vorbereiten
  const dataToUse = [...trendData.value]
  if (dataToUse.length === 1) {
    dataToUse.push({...dataToUse[0]})
  }

  const maxValue = Math.max(...dataToUse.map(d => d.total_menge || 0), 1)
  const minValue = Math.min(...dataToUse.map(d => d.total_menge || 0), 0)
  const range = maxValue - minValue || 1

  // Chart-Punkte berechnen
  const largeChartPoints = dataToUse.map((item, index) => ({
    x: largePadding + (index * (largeChartWidth - largePadding * 2)) / Math.max(dataToUse.length - 1, 1),
    y: largeChartHeight - largePadding - ((item.total_menge - minValue) / range) * (largeChartHeight - largePadding * 2)
  }))

  // SVG-Pfad berechnen
  let largePath = ''
  if (largeChartPoints.length === 1) {
    const point = largeChartPoints[0]
    largePath = 'M ' + largePadding + ' ' + point.y + ' L ' + (largeChartWidth - largePadding) + ' ' + point.y
  } else if (largeChartPoints.length > 1) {
    largePath = 'M ' + largeChartPoints[0].x + ' ' + largeChartPoints[0].y
    for (let i = 1; i < largeChartPoints.length; i++) {
      const prev = largeChartPoints[i - 1]
      const curr = largeChartPoints[i]
      const cpX1 = prev.x + (curr.x - prev.x) * 0.3
      const cpY1 = prev.y
      const cpX2 = curr.x - (curr.x - prev.x) * 0.3
      const cpY2 = curr.y
      largePath += ' C ' + cpX1 + ' ' + cpY1 + ', ' + cpX2 + ' ' + cpY2 + ', ' + curr.x + ' ' + curr.y
    }
  }

  // Area-Pfad berechnen
  let largeAreaPath = largePath
  if (largeChartPoints.length === 1) {
    const point = largeChartPoints[0]
    largeAreaPath = 'M ' + largePadding + ' ' + point.y + ' L ' + (largeChartWidth - largePadding) + ' ' + point.y + ' L ' + (largeChartWidth - largePadding) + ' ' + (largeChartHeight - largePadding) + ' L ' + largePadding + ' ' + (largeChartHeight - largePadding) + ' Z'
  } else if (largeChartPoints.length > 1) {
    const lastPoint = largeChartPoints[largeChartPoints.length - 1]
    const firstPoint = largeChartPoints[0]
    largeAreaPath += ' L ' + lastPoint.x + ' ' + (largeChartHeight - largePadding)
    largeAreaPath += ' L ' + firstPoint.x + ' ' + (largeChartHeight - largePadding)
    largeAreaPath += ' Z'
  }

  // Y-Achsen Ticks
  const largeTicks = []
  const tickCount = 8
  for (let i = 0; i <= tickCount; i++) {
    const value = Math.round(minValue + (range / tickCount) * i)
    const y = largeChartHeight - largePadding - ((value - minValue) / range) * (largeChartHeight - largePadding * 2)
    largeTicks.push({ value, y })
  }

  // Durchschnittslinie
  const avgValue = averages.value?.total_menge || 0
  const avgLineY = largeChartHeight - largePadding - ((avgValue - minValue) / range) * (largeChartHeight - largePadding * 2)

  // HTML-Komponenten erstellen
  const gridLines = largeTicks.map(tick => {
    return '<line x1="' + largePadding + '" y1="' + tick.y + '" x2="' + (largeChartWidth - largePadding) + '" y2="' + tick.y + '" stroke="rgba(255,255,255,0.1)" stroke-width="1"/>' +
           '<text x="' + (largePadding - 10) + '" y="' + (tick.y + 5) + '" text-anchor="end" fill="rgba(255,255,255,0.6)" font-size="14">' + tick.value + '</text>'
  }).join('')

  const xLabels = largeChartPoints.map((point, index) => {
    return '<text x="' + point.x + '" y="' + (largeChartHeight - largePadding + 20) + '" text-anchor="middle" fill="rgba(255,255,255,0.7)" font-size="12">' + (trendData.value[index]?.label || '') + '</text>'
  }).join('')

  const dataPoints = largeChartPoints.map((point, index) => {
    return '<circle cx="' + point.x + '" cy="' + point.y + '" r="8" fill="' + chartColor.value.value + '" stroke="white" stroke-width="3" class="chart-point" data-index="' + index + '"/>' +
           '<circle cx="' + point.x + '" cy="' + point.y + '" r="20" fill="transparent" class="chart-hover-area" data-index="' + index + '"/>'
  }).join('')

  const statsCards = [
    { value: Math.round(averages.value?.total_menge || 0), label: '⌀ Gesamtmenge' },
    { value: Math.round(averages.value?.instandsetzungen_menge || 0), label: '⌀ Instandsetzungen' },
    { value: Math.round(averages.value?.retouren_menge || 0), label: '⌀ Retouren' },
    { value: formatMinutes(averages.value?.nebentaetigkeiten_minuten || 0), label: '⌀ Nebentätigkeiten' }
  ].map(stat => {
    return '<div class="stat-card"><div class="stat-value">' + stat.value + '</div><div class="stat-label">' + stat.label + '</div></div>'
  }).join('')

  const tableRows = trendData.value.map(item => {
    return '<tr>' +
           '<td><strong>' + item.full_label + '</strong></td>' +
           '<td>' + item.total_menge + ' Stk. (' + item.total_auftraege + ' Aufträge)</td>' +
           '<td>' + item.instandsetzungen_menge + ' Stk. (' + item.instandsetzungen + ' Aufträge)</td>' +
           '<td>' + item.retouren_menge + ' Stk. (' + item.retouren + ' Aufträge)</td>' +
           '<td>' + formatMinutes(item.nebentaetigkeiten_minuten) + ' (' + item.nebentaetigkeiten_count + ' Einträge)</td>' +
           '</tr>'
  }).join('')

  const tooltipDataJson = JSON.stringify(trendData.value)
  const periodText = selectedPeriod.value === 'weekly' ? 'Wöchentlich' : 'Monatlich'
  const periodTextLong = selectedPeriod.value === 'weekly' ? 'Wöchentliche Entwicklung' : 'Monatliche Entwicklung'

  // HTML zusammenbauen
  let html = '<!DOCTYPE html>'
  html += '<html lang="de">'
  html += '<head>'
  html += '<meta charset="UTF-8">'
  html += '<meta name="viewport" content="width=device-width, initial-scale=1.0">'
  html += '<title>Trend-Analyse: ' + props.employee.name + ' - ' + periodText + '</title>'
  html += '<style>'
  html += 'body { margin: 0; padding: 20px; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%); font-family: \'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif; color: #ffffff; min-height: 100vh; }'
  html += '.container { max-width: 1200px; margin: 0 auto; background: rgba(30, 30, 40, 0.8); border-radius: 12px; padding: 30px; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3); }'
  html += '.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid rgba(76, 175, 80, 0.3); padding-bottom: 20px; }'
  html += '.title { font-size: 2rem; margin-bottom: 10px; color: #ffffff; }'
  html += '.subtitle { font-size: 1.2rem; color: rgba(255, 255, 255, 0.8); margin-bottom: 5px; }'
  html += '.period-info { font-size: 1rem; color: rgba(76, 175, 80, 0.9); }'
  html += '.chart-container { background: rgba(10, 10, 20, 0.5); border-radius: 8px; padding: 30px; margin-bottom: 30px; text-align: center; position: relative; }'
  html += '.chart-title { font-size: 1.3rem; margin-bottom: 20px; color: rgba(255, 255, 255, 0.9); }'
  html += '.chart-svg { border-radius: 8px; background: rgba(20, 20, 30, 0.3); }'
  html += '.chart-point { cursor: pointer; transition: all 0.3s ease; }'
  html += '.chart-point:hover { r: 10; filter: drop-shadow(0 0 8px rgba(255,255,255,0.8)); }'
  html += '.chart-hover-area { cursor: pointer; opacity: 0; }'
  html += '.chart-tooltip { position: fixed; background: rgba(0, 0, 0, 0.9); color: white; padding: 16px; border-radius: 12px; font-size: 14px; font-weight: 500; box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.2); backdrop-filter: blur(10px); z-index: 1000; min-width: 280px; pointer-events: none; }'
  html += '.tooltip-header { font-size: 16px; font-weight: bold; color: #4fc3f7; margin-bottom: 12px; text-align: center; border-bottom: 1px solid rgba(255, 255, 255, 0.1); padding-bottom: 8px; }'
  html += '.tooltip-content { display: flex; flex-direction: column; gap: 8px; }'
  html += '.tooltip-section { display: flex; flex-direction: column; gap: 6px; }'
  html += '.tooltip-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }'
  html += '.tooltip-label { color: rgba(255, 255, 255, 0.8); font-size: 13px; }'
  html += '.tooltip-value { color: white; font-weight: bold; font-size: 14px; }'
  html += '.total-row { background: rgba(79, 195, 247, 0.1); border-radius: 6px; padding: 8px 12px; margin: 2px 0; }'
  html += '.total-row .tooltip-value { color: #4fc3f7; font-size: 16px; }'
  html += '.productivity-row { background: rgba(129, 199, 132, 0.1); border-radius: 6px; padding: 8px 12px; margin: 2px 0; }'
  html += '.productivity-row .tooltip-value { color: #81c784; font-size: 15px; }'
  html += '.tooltip-divider { height: 1px; background: rgba(255, 255, 255, 0.1); margin: 8px 0; }'
  html += '.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px; }'
  html += '.stat-card { background: rgba(40, 40, 50, 0.6); border-radius: 8px; padding: 20px; text-align: center; }'
  html += '.stat-value { font-size: 2rem; font-weight: bold; color: rgba(76, 175, 80, 0.9); margin-bottom: 5px; }'
  html += '.stat-label { color: rgba(255, 255, 255, 0.7); font-size: 0.9rem; }'
  html += '.data-table { background: rgba(20, 20, 30, 0.5); border-radius: 8px; padding: 25px; overflow-x: auto; }'
  html += '.table-title { font-size: 1.3rem; margin-bottom: 20px; color: rgba(255, 255, 255, 0.9); text-align: center; }'
  html += 'table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }'
  html += 'th, td { padding: 12px; text-align: left; border-bottom: 1px solid rgba(255, 255, 255, 0.1); }'
  html += 'th { background: rgba(76, 175, 80, 0.2); color: rgba(255, 255, 255, 0.9); font-weight: bold; }'
  html += 'td { color: rgba(255, 255, 255, 0.8); }'
  html += '.close-button { position: fixed; top: 20px; right: 20px; background: rgba(244, 67, 54, 0.9); color: white; border: none; border-radius: 50%; width: 50px; height: 50px; font-size: 20px; cursor: pointer; box-shadow: 0 4px 15px rgba(244, 67, 54, 0.4); transition: all 0.3s ease; z-index: 1001; }'
  html += '.close-button:hover { background: rgba(244, 67, 54, 1); transform: scale(1.1); }'
  html += '</style>'
  html += '</head>'
  html += '<body>'
  html += '<button class="close-button" id="closeBtn">✕</button>'
  html += '<div class="container">'
  html += '<div class="header">'
  html += '<div class="title">Trend-Analyse</div>'
  html += '<div class="subtitle">' + props.employee.name + '</div>'
  html += '<div class="period-info">' + periodTextLong + '</div>'
  html += '</div>'
  html += '<div class="chart-container">'
  html += '<div class="chart-title">Mengen-Entwicklung</div>'
  html += '<div style="position: relative;">'
  html += '<svg id="mainChart" width="' + largeChartWidth + '" height="' + largeChartHeight + '" class="chart-svg">'
  html += '<defs>'
  html += '<linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">'
  html += '<stop offset="0%" style="stop-color:' + chartColor.value.value + ';stop-opacity:0.3" />'
  html += '<stop offset="100%" style="stop-color:' + chartColor.value.value + ';stop-opacity:0.05" />'
  html += '</linearGradient>'
  html += '</defs>'
  html += gridLines
  html += xLabels
  html += '<line x1="' + largePadding + '" y1="' + avgLineY + '" x2="' + (largeChartWidth - largePadding) + '" y2="' + avgLineY + '" stroke="rgba(255,255,255,0.4)" stroke-width="2" stroke-dasharray="8,4"/>'
  html += '<text x="' + (largeChartWidth - largePadding + 5) + '" y="' + (avgLineY + 5) + '" fill="rgba(255,255,255,0.6)" font-size="12">⌀ ' + (averages.value?.total_menge || 0) + '</text>'
  html += '<path d="' + largeAreaPath + '" fill="url(#chartGradient)"/>'
  html += '<path d="' + largePath + '" stroke="' + chartColor.value.value + '" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
  html += dataPoints
  html += '</svg>'
  html += '<div id="tooltip" class="chart-tooltip" style="display: none;">'
  html += '<div class="tooltip-header"></div>'
  html += '<div class="tooltip-content">'
  html += '<div class="tooltip-section">'
  html += '<div class="tooltip-row total-row">'
  html += '<span class="tooltip-label">📊 Gesamtmenge:</span>'
  html += '<span class="tooltip-value total-value"></span>'
  html += '</div>'
  html += '<div class="tooltip-row total-row">'
  html += '<span class="tooltip-label">📋 Aufträge gesamt:</span>'
  html += '<span class="tooltip-value"></span>'
  html += '</div>'
  html += '</div>'
  html += '<div class="tooltip-divider"></div>'
  html += '<div class="tooltip-section">'
  html += '<div class="tooltip-row">'
  html += '<span class="tooltip-label">🔧 Instandsetzungen:</span>'
  html += '<span class="tooltip-value"></span>'
  html += '</div>'
  html += '<div class="tooltip-row">'
  html += '<span class="tooltip-label">↩️ Retouren:</span>'
  html += '<span class="tooltip-value"></span>'
  html += '</div>'
  html += '<div class="tooltip-row">'
  html += '<span class="tooltip-label">⏱️ Nebentätigkeiten:</span>'
  html += '<span class="tooltip-value"></span>'
  html += '</div>'
  html += '</div>'
  html += '<div class="tooltip-divider"></div>'
  html += '<div class="tooltip-section">'
  html += '<div class="tooltip-row productivity-row">'
  html += '<span class="tooltip-label">⚡ Gesamtleistung:</span>'
  html += '<span class="tooltip-value"></span>'
  html += '</div>'
  html += '</div>'
  html += '</div>'
  html += '</div>'
  html += '</div>'
  html += '</div>'
  html += '<div class="stats-grid">'
  html += statsCards
  html += '</div>'
  html += '<div class="data-table">'
  html += '<div class="table-title">Detaillierte Aufschlüsselung</div>'
  html += '<table>'
  html += '<thead>'
  html += '<tr>'
  html += '<th>Zeitraum</th>'
  html += '<th>Gesamtmenge</th>'
  html += '<th>Instandsetzungen</th>'
  html += '<th>Retouren</th>'
  html += '<th>Nebentätigkeiten</th>'
  html += '</tr>'
  html += '</thead>'
  html += '<tbody>'
  html += tableRows
  html += '</tbody>'
  html += '</table>'
  html += '</div>'
  html += '</div>'
  html += '</body>'
  html += '<script>'
  html += 'document.addEventListener("DOMContentLoaded", function() {'
  html += 'const tooltip = document.getElementById("tooltip");'
  html += 'const chartHoverAreas = document.querySelectorAll(".chart-hover-area");'
  html += 'const chartPoints = document.querySelectorAll(".chart-point");'
  html += 'const closeBtn = document.getElementById("closeBtn");'
  html += 'const tooltipData = ' + tooltipDataJson + ';'
  html += 'closeBtn.addEventListener("click", function() {'
  html += 'window.close();'
  html += '});'
  html += 'document.addEventListener("keydown", function(e) {'
  html += 'if (e.key === "Escape") {'
  html += 'window.close();'
  html += '}'
  html += '});'
  html += 'function showTooltip(event, index) {'
  html += 'const item = tooltipData[index];'
  html += 'if (!item) return;'
  html += 'const totalAuftraege = item.instandsetzungen + item.retouren + item.nebentaetigkeiten_count;'
  html += 'const productivity = item.total_menge || 0;'
  html += 'tooltip.querySelector(".tooltip-header").textContent = item.full_label;'
  html += 'const tooltipValues = tooltip.querySelectorAll(".tooltip-value");'
  html += 'tooltipValues[0].textContent = item.total_menge + " Stk.";'
  html += 'tooltipValues[1].textContent = totalAuftraege + " Aufträge";'
  html += 'tooltipValues[2].textContent = item.instandsetzungen_menge + " Stk. (" + item.instandsetzungen + " Aufträge)";'
  html += 'tooltipValues[3].textContent = item.retouren_menge + " Stk. (" + item.retouren + " Aufträge)";'
  html += 'tooltipValues[4].textContent = formatMinutes(item.nebentaetigkeiten_minuten) + " (" + item.nebentaetigkeiten_count + " Einträge)";'
  html += 'tooltipValues[5].textContent = productivity + " Stk.";'
  html += 'const chartContainer = document.querySelector(".chart-container");'
  html += 'const chartRect = chartContainer.getBoundingClientRect();'
  html += 'const targetRect = event.target.getBoundingClientRect();'
  html += 'const tooltipRect = tooltip.getBoundingClientRect();'
  html += 'let left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);'
  html += 'let top = targetRect.top - tooltipRect.height - 15;'
  html += 'const windowWidth = window.innerWidth;'
  html += 'const windowHeight = window.innerHeight;'
  html += 'if (left < 10) left = 10;'
  html += 'if (left + tooltipRect.width > windowWidth - 10) left = windowWidth - tooltipRect.width - 10;'
  html += 'if (top < 10) top = targetRect.bottom + 15;'
  html += 'tooltip.style.left = left + "px";'
  html += 'tooltip.style.top = top + "px";'
  html += 'tooltip.style.display = "block";'
  html += '}'
  html += 'function hideTooltip() {'
  html += 'tooltip.style.display = "none";'
  html += '}'
  html += 'function formatMinutes(minutes) {'
  html += 'if (!minutes) return "0 Min.";'
  html += 'const hours = Math.floor(minutes / 60);'
  html += 'const mins = minutes % 60;'
  html += 'if (hours > 0) {'
  html += 'return hours + "h " + mins + "min";'
  html += '} else {'
  html += 'return mins + " Min.";'
  html += '}'
  html += '}'
  html += 'chartHoverAreas.forEach((area, index) => {'
  html += 'area.addEventListener("mouseenter", (e) => showTooltip(e, index));'
  html += 'area.addEventListener("mouseleave", hideTooltip);'
  html += '});'
  html += 'chartPoints.forEach((point, index) => {'
  html += 'point.addEventListener("mouseenter", (e) => showTooltip(e, index));'
  html += 'point.addEventListener("mouseleave", hideTooltip);'
  html += '});'
  html += '});'
  html += '</sc' + 'ript>'
  html += '</html>'

  return html
}

// Chart in neuem Fenster öffnen
const openChartInNewWindow = () => {
  if (!trendData.value || trendData.value.length === 0) {
    return
  }

  chartLoading.value = true

  try {
    const html = generateChartHtml()

    // Blob-URL erstellen um CSP-Probleme zu umgehen
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)

    const newWindow = window.open(url, '_blank',
      'width=1200,height=800,scrollbars=yes,resizable=yes')

    if (newWindow) {
      newWindow.focus()
      // URL nach kurzer Zeit freigeben
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 1000)
    }
  } catch (err) {
    console.error('Fehler beim Öffnen des Charts:', err)
    // Fallback: Versuche es mit der alten Methode
    try {
      const html = generateChartHtml()
      const newWindow = window.open('', '_blank',
        'width=1200,height=800,scrollbars=yes,resizable=yes')

      if (newWindow) {
        newWindow.document.write(html)
        newWindow.document.close()
        newWindow.focus()
      }
    } catch (fallbackErr) {
      console.error('Auch Fallback-Methode fehlgeschlagen:', fallbackErr)
    }
  } finally {
    chartLoading.value = false
  }
}

// Watchers
watch(() => props.employee?.id, fetchTrendData, { immediate: true })
watch(selectedPeriod, fetchTrendData)
watch(() => props.selectedMonth, fetchTrendData)
watch(() => props.selectedYear, fetchTrendData)

// Lifecycle
onMounted(() => {
  fetchTrendData()
})
</script>

<style scoped>
.management-trend-widget {
  background: rgba(25, 25, 30, 0.95) !important;
  border: 1px solid rgba(76, 175, 80, 0.2) !important;
}

.mini-toggle {
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.mini-btn {
  background: rgba(30, 30, 35, 0.8);
  color: rgba(255, 255, 255, 0.7);
  border: none;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 20px;
}

.mini-btn:hover {
  background: rgba(76, 175, 80, 0.2);
  color: rgba(255, 255, 255, 0.9);
}

.mini-btn-active {
  background: rgba(76, 175, 80, 0.8) !important;
  color: white !important;
}

.trend-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.chart-preview {
  display: flex;
  align-items: center;
  background: rgba(40, 40, 45, 0.6);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.preview-icon {
  margin-right: 16px;
}

.preview-text {
  flex: 1;
}

.preview-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
}

.preview-subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}

.period-summary {
  background: rgba(35, 35, 40, 0.7);
  border-radius: 6px;
  padding: 12px;
}

.period-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(76, 175, 80, 0.9);
  margin-bottom: 8px;
  text-align: center;
}

.period-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.period-item {
  text-align: center;
  background: rgba(45, 45, 50, 0.8);
  border-radius: 4px;
  padding: 8px 4px;
}

.period-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2px;
}

.period-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.trend-direction {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: rgba(35, 35, 40, 0.6);
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.averages-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.average-item {
  background: rgba(40, 40, 45, 0.7);
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  border: 1px solid rgba(76, 175, 80, 0.15);
}

.average-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(76, 175, 80, 0.9);
  margin-bottom: 2px;
}

.average-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .averages-grid {
    grid-template-columns: 1fr;
  }
}

/* Detaillierte Aufschlüsselung */
.detailed-breakdown {
  background: rgba(30, 30, 35, 0.8);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid rgba(96, 125, 139, 0.2);
}

.breakdown-header {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(96, 125, 139, 0.9);
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.breakdown-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.8rem;
}

.breakdown-label {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.breakdown-value {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.breakdown-value.highlight {
  color: rgba(76, 175, 80, 0.9);
  font-size: 0.9rem;
  font-weight: 700;
}

.breakdown-section {
  border-left: 2px solid rgba(96, 125, 139, 0.3);
  padding-left: 10px;
  margin: 6px 0;
}

.breakdown-subheader {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(96, 125, 139, 0.8);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.breakdown-row.productivity {
  border-top: 1px solid rgba(96, 125, 139, 0.2);
  padding-top: 8px;
  margin-top: 8px;
}

.breakdown-row.productivity .breakdown-value {
  color: rgba(255, 193, 7, 0.9);
  font-weight: 700;
}

/* Modal Styles */
.chart-modal {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  color: white;
}

.chart-modal-content {
  background: rgba(30, 30, 40, 0.8);
  border-radius: 12px;
  padding: 30px;
}

.modal-chart-container {
  background: rgba(10, 10, 20, 0.5);
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  position: relative;
}

.chart-wrapper {
  position: relative;
  display: inline-block;
}

.modal-chart-svg {
  border-radius: 8px;
  background: rgba(20, 20, 30, 0.3);
}

.modal-chart-point {
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-chart-point:hover {
  r: 10;
  filter: drop-shadow(0 0 8px rgba(255,255,255,0.8));
}

.modal-chart-hover-area {
  cursor: pointer;
  opacity: 0;
}

.modal-chart-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  z-index: 2000;
  min-width: 280px;
  pointer-events: none;
}

.modal-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.modal-stat-card {
  background: rgba(40, 40, 50, 0.6);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.modal-data-table {
  background: rgba(20, 20, 30, 0.5);
  border-radius: 8px;
  padding: 25px;
}

/* Details Modal Styles */
.details-modal {
  background: rgba(25, 25, 30, 0.98) !important;
}

.summary-item {
  text-align: center;
  padding: 8px;
}

.summary-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.details-table {
  background: transparent !important;
}

.details-table :deep(th) {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 600 !important;
}

.details-table :deep(td) {
  color: rgba(255, 255, 255, 0.85) !important;
}

.details-table :deep(tr:hover) {
  background: rgba(76, 175, 80, 0.08) !important;
}

</style>
